import db from "./offlineDb";
import { postattendance } from "utils/requests/attendance/attendancepost";

const SYNC_LOCK_NAME = "askuala-attendance-sync";
// Without Web Locks, a record left in "syncing" this long is treated as abandoned
const STALE_SYNC_MS = 5 * 60 * 1000;

let activeFlush = null;

// Puts "syncing" records that started before staleBefore back in the queue
async function releaseStaleRecords(staleBefore) {
  await db.attendanceQueue
    .where("status")
    .equals("syncing")
    .filter((item) => !item.syncStartedAt || item.syncStartedAt < staleBefore)
    .modify({ status: "pending", syncStartedAt: null });
}

// Marks a record as syncing only if it is still pending
async function claimRecord(id) {
  return db.transaction("rw", db.attendanceQueue, async () => {
    const item = await db.attendanceQueue.get(id);
    if (!item || item.status !== "pending") return null;
    await db.attendanceQueue.update(id, { status: "syncing", syncStartedAt: Date.now() });
    return item;
  });
}

async function sendPendingRecords(staleBefore) {
  await releaseStaleRecords(staleBefore);

  const ids = await db.attendanceQueue.where("status").equals("pending").primaryKeys();
  let synced = 0;

  for (const id of ids) {
    const item = await claimRecord(id);
    if (!item) continue;

    try {
      const result = await postattendance(item.payload);
      if (result.success) {
        await db.attendanceQueue.delete(id);
        synced++;
      } else {
        await db.attendanceQueue.update(id, { status: "pending", syncStartedAt: null });
      }
    } catch {
      await db.attendanceQueue.update(id, { status: "pending", syncStartedAt: null });
    }
  }

  return synced;
}

// Flush all pending attendance records to the server.
// Returns the number of records synced. Calls made while a flush is already running in this
// tab share it and get its result instead of starting a second one.
export function flushAttendanceQueue() {
  if (activeFlush) return activeFlush;

  // The lock holder is the only syncer across tabs, so every "syncing" record it finds was
  // left behind by a closed tab and can be retried right away.
  const run = navigator.locks?.request
    ? navigator.locks.request(SYNC_LOCK_NAME, () => sendPendingRecords(Infinity))
    : sendPendingRecords(Date.now() - STALE_SYNC_MS);

  activeFlush = run.finally(() => {
    activeFlush = null;
  });
  return activeFlush;
}

// Counts records not yet on the server, including ones stuck in "syncing"
export async function getPendingCount() {
  return db.attendanceQueue.where("status").anyOf(["pending", "syncing"]).count();
}

// Removes a queued submission unless it is being sent right now. Returns whether it was removed.
export async function deleteQueuedSubmission(id) {
  return db.transaction("rw", db.attendanceQueue, async () => {
    const item = await db.attendanceQueue.get(id);
    if (!item || item.status === "syncing") return false;
    await db.attendanceQueue.delete(id);
    return true;
  });
}

// Lists queued submissions oldest first, with how many students got each attendance type
export async function getQueuedSubmissions() {
  const items = await db.attendanceQueue.orderBy("createdAt").toArray();
  return items.map(({ id, date, class: classLabel, status, createdAt, payload = [] }) => {
    const typeCounts = { present: 0, absent: 0, tardy: 0, permitted: 0 };
    payload.forEach((record) => {
      if (record.type in typeCounts) typeCounts[record.type] += 1;
    });
    return {
      id,
      date,
      classLabel,
      status,
      createdAt,
      studentCount: payload.length,
      typeCounts,
    };
  });
}
